import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { Check, ChevronUp, Minus, TriangleAlert, X } from "lucide-react";
import { useEffect, useState } from "react";

import { api, type DossierStepId } from "../api/client";
import { attentionSelector, designateSoon } from "../components/attention";
import { Disclosure } from "../components/panel";
import { motion, Motion, Presence } from "../components/presence";
import { Button, IconButton } from "../components/ui";
import { Waiting } from "../components/Waiting";
import { k, lang, t, tc } from "../i18n/i18n";
import { formatBytes } from "../map/zones/storage";
import { useUi } from "../state/ui";
import {
  closeGenesis,
  type Genesis,
  genesisBusy,
  reattachGenesis,
  retryGenesis,
  setGenesisCompact,
  useGenesis,
} from "./dossierGenesis";
import { openMissing } from "./DossierExportMenu";

/** Carte de la genèse du dossier d'exercice (demande d'Alexis du 18/10/2026, registre F37), posée en bas à droite au
 *  premier plan sans rien bloquer : petite pile de feuilles qui s'assemblent, parties du dossier cochées au fur et à
 *  mesure que le serveur les produit, barre de la vraie progression, temps écoulé et temps restant (quand des genèses
 *  du même niveau ont été mesurées), puis « Dossier enregistré » et le nom du fichier ; en cas d'échec, un message
 *  clair et « Réessayer », désigné. « Réduire » la ramène à une pastille, la genèse continue. Apparition et
 *  disparition en fondu glissé (Presence) ; les étapes se replient à la fin. Annonce de l'avancement aux lecteurs
 *  d'écran par une zone `status`. Documents séparés : la même carte, sans étapes. */

/** Cible de « Réessayer » (désignée à l'échec). */
export const GENESIS_RETRY = "genesis-retry";
/** Délai avant l'apparition : un refus immédiat (préalable manquant) ne fait pas clignoter la carte. */
const APPEAR_MS = 250;

/** Parties du dossier, dans l'ordre du dossier ; la page de garde porte le sommaire, elle est composée en dernier. */
const PARTS: { id: DossierStepId; label: string; note?: string }[] = [
  { id: "cover", label: k("Page de garde"), note: k("composée à la fin") },
  { id: "country", label: k("Fiche pays") },
  { id: "briefing", label: k("Mise en ambiance") },
  // Corps d'armée (docs/08 §25) : l'OPLAN du corps, juste avant l'OPO ; absent des autres niveaux.
  { id: "oplan", label: k("OPLAN et ses annexes") },
  { id: "order", label: k("OPO et ses annexes") },
  { id: "odb_ami", label: k("Ordre de bataille AMI") },
  { id: "odb_eni", label: k("Ordre de bataille ENI") },
  { id: "map", label: k("Carte de situation") },
  { id: "merge", label: k("Assemblage du fichier") },
];

/** Libellé du document séparé préparé. */
const DOCUMENT: Record<Exclude<Genesis["kind"], "dossier">, string> = {
  country: k("Fiche pays (PDF)"),
  part: k("Mise en ambiance (PDF)"),
  oplan: k("OPLAN (PDF)"),
  order: k("OPO (PDF)"),
};

/** Durée lisible : « 0:42 », « 1:05 ». */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

type StepStatus = "pending" | "running" | "done" | "skipped";

/** Parties de ce dossier : celles que le serveur publie (l'OPLAN au corps d'armée seulement). */
function partsOf(genesis: Genesis): typeof PARTS {
  const published = genesis.job?.steps;
  if (!published?.length) return PARTS.filter((p) => p.id !== "oplan");
  return PARTS.filter((p) => published.some((s) => s.id === p.id));
}

function stepsOf(genesis: Genesis): Record<DossierStepId, StepStatus> {
  const out = Object.fromEntries(PARTS.map((p) => [p.id, "pending"])) as Record<DossierStepId, StepStatus>;
  for (const step of genesis.job?.steps ?? []) out[step.id] = step.status;
  return out;
}

/** Titre de la carte selon la phase, toujours affiché par `<Waiting>` (points animés des libellés d'attente). */
// ui-check: ignore-start — libellés rendus par <Waiting> dans GenesisCard (le contrôle ne suit pas l'appel).
function title(genesis: Genesis): string {
  const dossier = genesis.kind === "dossier";
  switch (genesis.phase) {
    case "saved":
      return dossier ? t("Dossier d'exercice enregistré") : t("Document enregistré");
    case "failed":
      return dossier ? t("Dossier non préparé") : t("Document non téléchargé");
    case "saving":
      return t("Enregistrement du fichier…");
    default:
      return dossier ? t("Dossier en cours de genèse…") : t("Préparation du document…");
  }
}
// ui-check: ignore-end

/** Annonce aux lecteurs d'écran : l'étape en cours, puis le résultat. */
export function announcement(genesis: Genesis): string {
  if (genesis.phase === "saved") return t("Dossier enregistré : {name}", { name: genesis.filename ?? "" });
  if (genesis.phase === "failed") return genesis.error ?? title(genesis);
  if (genesis.kind !== "dossier") return title(genesis);
  if (genesis.job?.state === "queued") return t("Un autre dossier est en cours d'assemblage : celui-ci attend son tour");
  const running = partsOf(genesis).find((p) => stepsOf(genesis)[p.id] === "running");
  return running ? t("Étape en cours : {name}", { name: t(running.label) }) : title(genesis);
}

/** Petite pile de feuilles : elles glissent l'une après l'autre sur la pile pendant la genèse ; à la fin, la pile se
 *  pose et reçoit une coche (ou un signal d'échec). */
function SheetStack({ phase }: { phase: Genesis["phase"] }) {
  const busy = phase === "starting" || phase === "running" || phase === "saving";
  return (
    <div aria-hidden className="relative h-14 w-12 shrink-0" data-genesis={busy ? "busy" : phase}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={clsx(
            "absolute top-0 left-0.5 flex h-12 w-9 flex-col gap-1 rounded-sm border border-line-strong bg-surface px-1.5 pt-1.5 shadow-card",
            busy ? "gx-genesis-sheet" : "gx-genesis-settled",
          )}
          style={{ ["--gx-sheet" as string]: i }}
        >
          <span className="h-0.5 w-full rounded-full bg-brand/70" />
          <span className="h-0.5 w-full rounded-full bg-line-strong" />
          <span className="h-0.5 w-4/5 rounded-full bg-line-strong" />
          <span className="h-0.5 w-full rounded-full bg-line-strong" />
          <span className="h-0.5 w-3/5 rounded-full bg-line-strong" />
        </span>
      ))}
      {phase === "saved" && (
        <span className="gx-genesis-pop absolute right-0 bottom-0 flex size-5 items-center justify-center rounded-full bg-ok text-white shadow-chip">
          <Check size={12} strokeWidth={3} />
        </span>
      )}
      {phase === "failed" && (
        <span className="gx-genesis-pop absolute right-0 bottom-0 flex size-5 items-center justify-center rounded-full bg-danger text-white shadow-chip">
          <TriangleAlert size={11} strokeWidth={2.5} />
        </span>
      )}
    </div>
  );
}

function StepIcon({ status, halted }: { status: StepStatus; halted: boolean }) {
  // Genèse en échec : l'étape où elle s'est arrêtée est signalée.
  if (status === "running" && halted) return <TriangleAlert size={13} className="text-danger" />;
  if (status === "done") return <Check size={14} strokeWidth={2.5} className="gx-genesis-pop text-ok" />;
  if (status === "skipped") return <Minus size={14} className="text-ink-muted" />;
  if (status === "running") return <span className="gx-genesis-running size-2.5 rounded-full bg-run" />;
  return <span className="size-2.5 rounded-full border border-line-strong" />;
}

/** Contenu de la carte pour un état donné (rendu testé sans navigateur). `now` : horloge du poste (ms). */
export function GenesisCard({ genesis, now }: { genesis: Genesis; now: number }) {
  const { phase, job } = genesis;
  const busy = genesisBusy(genesis);
  const dossier = genesis.kind === "dossier";
  const steps = stepsOf(genesis);
  const elapsed = ((genesis.endedAt ?? now) - genesis.startedAt) / 1000;
  const known = dossier && job !== null;
  const share = phase === "saved" ? 100 : known ? Math.min(99, Math.floor(job.progress * 100)) : 0;
  const queued = job?.state === "queued";
  const remaining = phase === "running" && job?.state === "running" ? job.remaining_s : null;
  return (
    <div
      role="region"
      aria-label={t("Genèse du dossier d'exercice")}
      className="pointer-events-auto w-[22rem] max-w-[calc(100vw-2rem)] rounded-xl border border-line bg-surface shadow-float"
    >
      <div className="flex items-start gap-3 pt-3.5 pr-2 pb-3 pl-4">
        <SheetStack phase={phase} />
        <div className="min-w-0 flex-1 pt-0.5">
          <p className="text-ui leading-snug font-semibold text-ink">
            <Waiting>{title(genesis)}</Waiting>
          </p>
          <p className="mt-0.5 text-meta leading-snug break-words text-ink-muted">
            {genesis.kind === "dossier" ? genesis.name : `${t(DOCUMENT[genesis.kind])} · ${genesis.name}`}
          </p>
          {phase === "saved" && genesis.filename && (
            <p className={clsx(motion("fade"), "mt-1 text-meta leading-snug font-medium break-all text-ink-soft")}>{genesis.filename}</p>
          )}
        </div>
        {busy ? (
          <IconButton size="sm" onClick={() => setGenesisCompact(true)} title={tc("genèse", "Réduire")} aria-label={tc("genèse", "Réduire")}>
            <Minus size={14} />
          </IconButton>
        ) : (
          <IconButton size="sm" onClick={closeGenesis} title={t("Fermer")} aria-label={t("Fermer")}>
            <X size={14} />
          </IconButton>
        )}
      </div>

      {dossier && (
        <Disclosure open={phase !== "saved"}>
          <ol className="pb-2" aria-label={t("Parties du dossier")}>
            {partsOf(genesis).map((part) => {
              const status = steps[part.id];
              return (
                <li key={part.id} className="flex min-h-7 items-center gap-2.5 px-4 py-1" data-step={part.id} data-status={status}>
                  <span className="flex size-4 shrink-0 items-center justify-center">
                    <StepIcon status={status} halted={phase === "failed"} />
                  </span>
                  <span
                    className={clsx(
                      "min-w-0 flex-1 text-ui leading-snug",
                      status === "running"
                        ? phase === "failed"
                          ? "font-medium text-danger"
                          : "font-medium text-ink"
                        : status === "pending"
                          ? "text-ink-muted"
                          : "text-ink-soft",
                    )}
                  >
                    {t(part.label)}
                  </span>
                  {status === "running" && busy && (
                    <span className="shrink-0 text-meta text-run">
                      <Waiting>{t("En cours…")}</Waiting>
                    </span>
                  )}
                  {status === "skipped" && <span className="shrink-0 text-meta text-ink-muted">{t("Omise")}</span>}
                  {status === "pending" && part.note && busy && (
                    <span className="shrink-0 text-meta text-ink-muted">{t(part.note)}</span>
                  )}
                </li>
              );
            })}
          </ol>
        </Disclosure>
      )}

      <div className="px-4 pb-3.5">
        {phase !== "failed" && (
          <>
            <div
              className="h-1.5 overflow-hidden rounded-full bg-line"
              role="progressbar"
              aria-label={t("Avancement de la genèse du dossier")}
              aria-valuemin={known || phase === "saved" ? 0 : undefined}
              aria-valuemax={known || phase === "saved" ? 100 : undefined}
              aria-valuenow={known || phase === "saved" ? share : undefined}
            >
              {known || phase === "saved" ? (
                <div
                  className={clsx("gx-veil-bar h-full rounded-full", phase === "saved" ? "bg-ok" : "bg-run")}
                  style={{ width: `${Math.max(share, 3)}%` }}
                />
              ) : (
                <div className="gx-veil-indeterminate h-full w-1/3 rounded-full bg-run" />
              )}
            </div>
            <div className="mt-2 flex items-baseline gap-2 text-meta leading-snug text-ink-muted tabular-nums">
              <span className="min-w-0 flex-1">
                {phase === "saved" ? (
                  genesis.job?.size ? (
                    t("Prêt en {time} · {size}", { time: clock(elapsed), size: formatBytes(genesis.job.size, lang()) })
                  ) : (
                    t("Prêt en {time}", { time: clock(elapsed) })
                  )
                ) : queued ? (
                  <Waiting>{t("En attente : un autre dossier est en cours d'assemblage…")}</Waiting>
                ) : (
                  <>
                    {t("Temps écoulé : {time}", { time: clock(elapsed) })}
                    {remaining !== null && remaining > 0 && <> · {t("environ {time} restantes", { time: clock(remaining) })}</>}
                    {remaining === 0 && (
                      <>
                        {" · "}
                        <Waiting>{t("Encore quelques instants…")}</Waiting>
                      </>
                    )}
                  </>
                )}
              </span>
              {known && <span className="shrink-0 font-medium text-ink-soft">{share} %</span>}
            </div>
          </>
        )}
        {phase === "failed" && (
          <div className={motion("fade")}>
            <p className="text-meta leading-snug break-words text-danger">{genesis.error}</p>
            <div className="mt-3 flex items-center justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={closeGenesis}>
                {t("Fermer")}
              </Button>
              <Button size="sm" data-attention={GENESIS_RETRY} onClick={() => void retry()}>
                {t("Réessayer")}
              </Button>
            </div>
          </div>
        )}
      </div>
      <p className="sr-only" role={phase === "failed" ? "alert" : "status"} aria-live={phase === "failed" ? "assertive" : "polite"}>
        {announcement(genesis)}
      </p>
    </div>
  );
}

/** Pastille de la carte réduite : avancement, un clic la rouvre. */
export function GenesisChip({ genesis }: { genesis: Genesis }) {
  const share = genesis.job ? Math.min(99, Math.floor(genesis.job.progress * 100)) : null;
  return (
    <button
      type="button"
      onClick={() => setGenesisCompact(false)}
      className="pointer-events-auto flex h-9 items-center gap-2 rounded-full border border-line bg-surface pr-2.5 pl-3.5 text-ui text-ink shadow-float hover:bg-hover"
      title={t("Afficher la genèse du dossier")}
    >
      <span className="gx-genesis-running size-2 rounded-full bg-run" />
      <Waiting>{genesis.kind === "dossier" ? t("Dossier en cours de genèse…") : t("Préparation du document…")}</Waiting>
      {share !== null && <span className="text-meta font-medium text-ink-soft tabular-nums">{share} %</span>}
      <ChevronUp size={14} className="text-ink-muted" />
    </button>
  );
}

/** « Réessayer » : un préalable manquant entre-temps ouvre sa vue et y désigne l'action attendue. */
async function retry(): Promise<void> {
  const { missing } = await retryGenesis();
  if (missing) {
    const { setView, notify } = useUi.getState();
    openMissing(missing, { setView, notify });
  }
}

/** Horloge du poste, relue chaque seconde tant que `active`. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

/** Hôte de la carte (au-dessus de l'espace de travail, App.tsx ; en bas à droite, au-dessus de la barre basse de
 *  la carte) : rattachement à la genèse du dossier ouvert,
 *  apparition différée, désignation de « Réessayer » à l'échec. */
export function DossierGenesisHost() {
  const genesis = useGenesis((s) => s.genesis);
  const exerciseId = useUi((s) => s.exerciseId);
  const { data: exercises } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises, enabled: Boolean(exerciseId) });
  const name = exercises?.find((e) => e.id === exerciseId)?.name;
  const busy = genesisBusy(genesis);
  const now = useNow(busy);
  const [ready, setReady] = useState(false);

  // Page rechargée, dossier rouvert : la genèse en cours de ce dossier reprend sa carte.
  useEffect(() => {
    if (exerciseId && name !== undefined) void reattachGenesis(exerciseId, name);
  }, [exerciseId, name]);

  const present = Boolean(genesis);
  useEffect(() => {
    if (!present) {
      setReady(false);
      return;
    }
    const timer = window.setTimeout(() => setReady(true), APPEAR_MS);
    return () => window.clearTimeout(timer);
  }, [present]);

  const failed = genesis?.phase === "failed" && ready;
  useEffect(() => {
    if (!failed) return;
    return designateSoon(attentionSelector(GENESIS_RETRY), "button", 1200);
  }, [failed]);

  const shown = Boolean(genesis) && ready;
  return (
    <div className="pointer-events-none fixed right-4 bottom-16 z-40 flex flex-col items-end">
      <Presence show={shown && !genesis?.compact}>
        {genesis && (
          <Motion origin="bottom">
            <GenesisCard genesis={genesis} now={now} />
          </Motion>
        )}
      </Presence>
      <Presence show={shown && Boolean(genesis?.compact)}>
        {genesis && (
          <Motion origin="bottom">
            <GenesisChip genesis={genesis} />
          </Motion>
        )}
      </Presence>
    </div>
  );
}
